import React from "react";
import { compose, withAppContext, withState, withActions } from "containers/HOC";
import { Link } from "react-router-dom";
import * as utils from "common/utils";
import Panel from "components/Panel/Panel";
import PanelBody from "components/Panel/PanelBody";
import SortableTable from "components/new/SortableTable";
import SortableTableHeader from "components/new/SortableTableHeader";
import ActionLink from "components/ActionLink";
import { confirm } from "containers/App/_actions";
import { deleteFarmGroup } from "containers/FarmGroups/_actions";
import { getMyFarmGroups } from "./_actions";
import Alert from "components/Alert";
import { modalInlineClose } from "containers/App/_actions";
import ZendeskLink from "components/Help/ZendeskLink";

const MyFarmGroups = ({ online, myFarmGroupsLoading, myFarmGroups, confirm, deleteFarmGroup, getMyFarmGroups, authContext, refData }) => {
    React.useEffect(() => {
        if (process.env.REACT_APP_DISABLE_FARM_GROUPS !== "true") {
            getMyFarmGroups();
        }
    }, [getMyFarmGroups, online]);

    const data = myFarmGroups && myFarmGroups.filter((farmGroup) => farmGroup.role === "Admin" && farmGroup.id !== authContext.accountId);

    if (!data || data.length === 0) return null;

    const confirmDeleteFarmGroup = (farmGroup) => () => {
        confirm(`Are you sure you want to delete this farm group?`, () =>
            deleteFarmGroup(farmGroup.id)
                .then(() => getMyFarmGroups())
                .catch()
        );
    };

    const alertText = (
        <div>
            <span>
                The following is a list of groups that you are the administrator of. You can view aggregated reporting and benchmarking for the group by selecting from the list. To see a user guide on farm groups,{" "}
                <b>
                    <ZendeskLink title="click here" url="https://support.overseer.org.nz/hc/en-us/articles/900000809586" target="_blank" rel="noopener noreferrer" id="lnk-open-guide" />
                </b>
            </span>
        </div>
    );

    const { farmGroupVisibility = [] } = refData;

    return (
        <>
            <Panel title="Farm groups" iconClassName="IconLink--group" loading={online && myFarmGroupsLoading}>
                <Alert type="info" text={alertText} />
                <PanelBody>
                    <SortableTable data={data} defaultSortCriteria={{ fieldName: "createdOn", fieldType: "date", desc: true }}>
                        {({ data, sortCriteria, handleSort }) => {
                            return (
                                <table>
                                    <thead>
                                        <tr>
                                            <SortableTableHeader label="Name" fieldName="name" sortCriteria={sortCriteria} handleSort={handleSort} />
                                            <SortableTableHeader label="Created" fieldName="createdOn" sortCriteria={sortCriteria} handleSort={handleSort} date />
                                            <SortableTableHeader label="Role" fieldName="role" sortCriteria={sortCriteria} handleSort={handleSort} />
                                            <SortableTableHeader label="Visibility" fieldName="farmGroupVisibility" sortCriteria={sortCriteria} handleSort={handleSort} />
                                            <SortableTableHeader label="Invited" fieldName="invitedCount" sortCriteria={sortCriteria} handleSort={handleSort} number />
                                            <SortableTableHeader label="Members" fieldName="memberCount" sortCriteria={sortCriteria} handleSort={handleSort} number />
                                            <th className="th--shrink"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.map((farmGroup) => {
                                            const isFarmGroupAdmin = farmGroup.role === "Admin";
                                            return (
                                                <tr key={farmGroup.id} className="hover">
                                                    <td>
                                                        <Link to={`/app/farmGroup/${farmGroup.id}/aggregated`} id={`view-farm-group-${farmGroup.id}`} className="u-textBold">
                                                            {farmGroup.name}
                                                        </Link>
                                                    </td>
                                                    <td>{utils.dateOnly(farmGroup.createdOn)}</td>
                                                    <td>{farmGroup.role}</td>
                                                    <td>{utils.valueToText(farmGroupVisibility, farmGroup.farmGroupVisibility)}</td>
                                                    <td>{farmGroup.numInvited}</td>
                                                    <td>{farmGroup.numMembers}</td>
                                                    <td>
                                                        {isFarmGroupAdmin && online && farmGroup.numMembers === 0 && farmGroup.id !== authContext.accountId && (
                                                            <ActionLink id={`delete-${farmGroup.id}`} onClick={confirmDeleteFarmGroup(farmGroup)} className="IconLink--cross-circle u-link">
                                                                Delete
                                                            </ActionLink>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            );
                        }}
                    </SortableTable>
                </PanelBody>
            </Panel>
        </>
    );
};

export default compose(
    withAppContext,
    withState((state) => ({
        myFarmGroups: state.dashboard.myFarmGroups,
        myFarmGroupsLoading: state.dashboard.myFarmGroupsLoading,
    })),
    withActions({ getMyFarmGroups, confirm, deleteFarmGroup, modalInlineClose })
)(MyFarmGroups);
